import { ACTIVE_STATUSES } from '@/api/competitions'
import { LIFECYCLE_MAX_COMPETITIONS } from '@/api/revenueLeakage'

/** What the picker needs of a competition (the competitions list's row). */
export interface PickableCompetition {
  competitionId: string
  name: string
  status: string
  category: string | null
  startAt: string | null
}

/** How many the panel compares when it opens. */
export const DEFAULT_COMPARED = 3

/** Newest launch first; a competition with no launch date cannot be drawn, so it is left out. */
export function byLaunch<T extends PickableCompetition>(competitions: readonly T[]): T[] {
  return competitions.filter((competition) => competition.startAt !== null).sort((a, b) => (b.startAt ?? '').localeCompare(a.startAt ?? '') || a.competitionId.localeCompare(b.competitionId))
}

/**
 * The panel's opening comparison: the most recently launched live competition and the
 * newest launches of its category before it, three in all — so a competition on sale is
 * read against its own predecessors. With no live one, the three newest launches.
 */
export function defaultSelection(competitions: readonly PickableCompetition[]): string[] {
  const launched = byLaunch(competitions)
  const live = launched.find((competition) => ACTIVE_STATUSES.includes(competition.status))
  if (!live) return launched.slice(0, DEFAULT_COMPARED).map((competition) => competition.competitionId)
  // Its predecessors only: a newer one of its category (scheduled, say) is no yardstick for it.
  const before = launched.filter(
    (competition) => competition !== live && competition.category === live.category && (competition.startAt ?? '') <= (live.startAt ?? ''),
  )
  return [live, ...before].slice(0, DEFAULT_COMPARED).map((competition) => competition.competitionId)
}

/** `ids` with `id` added (at the end, up to the limit) or taken out. */
export function toggled(ids: readonly string[], id: string): string[] {
  if (ids.includes(id)) return ids.filter((each) => each !== id)
  return ids.length >= LIFECYCLE_MAX_COMPETITIONS ? [...ids] : [...ids, id]
}
